import { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import pluralize from "pluralize"
import { Card, CardContent, CardHeader, CardActions, Button, Checkbox, FormGroup, FormControlLabel, MenuItem, TextField, Typography } from "@mui/material"

const MINUTE = 60
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEK = 7 * DAY

const MULTIPLIERS = {
  minute: MINUTE,
  hour: HOUR,
  day: DAY,
  week: WEEK,
}

function getValueInSeconds(value, unit) {
  return value * MULTIPLIERS[unit]
}

export default function AccountSettings({ account, sessionSettings: { duration, preventRolling }, update, add }) {
  const [durationValue, durationUnit] = useMemo(() => {
    if (duration === WEEK || duration === null) {
      return [1, "week"]
    }
    if (duration % DAY === 0) {
      return [duration / DAY, "day"]
    }
    if (duration % HOUR === 0) {
      return [duration / HOUR, "hour"]
    }

    return [Math.floor(duration / MINUTE), "minute"]
  }, [duration])

  const [customSessions, setCustomSessions] = useState(duration !== null || preventRolling !== null)
  const [customDurationValue, setCustomDurationValue] = useState(durationValue)
  const [customDurationUnit, setCustomDurationUnit] = useState(durationUnit)
  const [customPreventRolling, setCustomPreventRolling] = useState(!!preventRolling)

  const error = useMemo(() => {
    const valueInSeconds = getValueInSeconds(customDurationValue, customDurationUnit)
    if (valueInSeconds < 30 * MINUTE || valueInSeconds > 1 * WEEK) {
      return "Value must be between 30 minutes and a week."
    }
  }, [customDurationValue, customDurationUnit])

  const description = useMemo(() => {
    if (customSessions && (customDurationValue !== durationValue || customDurationUnit !== durationUnit || customPreventRolling !== preventRolling)) {
      if (customPreventRolling) {
        return `Members will be forced to sign-in every ${customDurationValue > 1 ? `${customDurationValue} ` : ""}${pluralize(customDurationUnit, customDurationValue)}.`
      } else {
        return `Members will need to use the application at least every ${customDurationValue > 1 ? `${customDurationValue} ` : ""}${pluralize(customDurationUnit, customDurationValue)} or be signed-out.`
      }
    }

    if (!customSessions && (duration !== null || preventRolling !== null)) {
      return "Account will use default settings."
    }
  }, [customSessions, customDurationValue, durationValue, customDurationUnit, durationUnit, customPreventRolling, preventRolling, duration])

  const validateDurationValue = useCallback((event) => {
    if (event.target.value === "") {
      setCustomDurationValue("")
    }
    const intValue = Number.parseInt(event.target.value, 10)
    if (!Number.isNaN(intValue)) {
      setCustomDurationValue(intValue)
    }
  }, [setCustomDurationValue])

  const save = useCallback(() => {
    let payload
    if (customSessions) {
      const duration = getValueInSeconds(customDurationValue, customDurationUnit)
      payload = { duration, preventRolling: customPreventRolling }
    } else {
      payload = { duration: null, preventRolling: null }
    }

    update(`/api/${account}/sessions`, payload, account, {
      verb: "PUT",
      reloadData: "sessionSettings",
    }, (error) => {
      if (error) {
        add("error", "Unknown Error", "An unkown error has happened. Please try again later.")
      } else {
        add("success", "Session Settings Updated", "Sessions settings updated successfully")
      }
    })
  }, [account, add, update, customSessions, customDurationValue, customDurationUnit, customPreventRolling])

  return <Card className="modules_account-settings" style={{ maxWidth: 400, margin: "1rem auto" }}>
    <CardHeader title="User Sessions" />
    <CardContent>
      <Typography variant="body2" component="p">
        Sessions help keep a member logged in before automatically logging them out; any subsequent activity re-activates the sessions.
        <br />
        <br />
        <span style={{ fontStyle: "italic" }}>Changing these settings is not recommended.</span>
        <br />
        <br />
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={<Checkbox checked={customSessions} onChange={event => { setCustomSessions(event.target.checked) }} />}
          label="Override default session behavior"
        />
        {customSessions && <div style={{ marginLeft: "2rem" }}>
          <TextField
            variant="standard"
            error={!!error}
            helperText={error}
            label="Session Length"
            value={customDurationValue}
            onChange={validateDurationValue}
          />
          <TextField
            variant="standard"
            error={!!error}
            label=" "
            select
            value={customDurationUnit}
            onChange={event => { setCustomDurationUnit(event.target.value) }}
          >
            <MenuItem value="minute">
              {pluralize("Minute", customDurationValue)}
            </MenuItem>
            <MenuItem value="hour">
              {pluralize("Hour", customDurationValue)}
            </MenuItem>
            <MenuItem value="day">
              {pluralize("Day", customDurationValue)}
            </MenuItem>
            <MenuItem value="week">
              {pluralize("Week", customDurationValue)}
            </MenuItem>
          </TextField>
          <br />
          <br />
          <FormControlLabel
            control={<Checkbox checked={customPreventRolling} onChange={event => { setCustomPreventRolling(event.target.checked) }} />}
            label="Disable rolling sessions"
            style={{ marginBottom: 0 }}
          />
        </div>}
      </FormGroup>
      <br />
      {description && <Typography>{description}</Typography>}
    </CardContent>
    <CardActions style={{ flexDirection: "row-reverse" }}>
      <Button
        color="primary"
        disabled={!description || !!error}
        onClick={save}
      >
        Save Changes
      </Button>
    </CardActions>
  </Card>
}

AccountSettings.propTypes = {
  account: PropTypes.string.isRequired,
  sessionSettings: PropTypes.shape({
    duration: PropTypes.number,
    preventRolling: PropTypes.bool,
  }),
  update: PropTypes.func.isRequired,
  add: PropTypes.func.isRequired,
}
